import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DashboardLayout } from '../components/DashboardLayout';
import { TemplateList } from '../components/deliverables/TemplateList';
import { CreateTemplateModal } from '../components/deliverables/CreateTemplateModal';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Filter, FileText, CheckCircle, Users, Lock, Search, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DeliverableTemplate, CreateTemplateData, DeliverableCategory } from '../types/deliverable';
import { Lead } from '../types/lead';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const DeliverableTemplatesPage: React.FC = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<DeliverableCategory | 'ALL'>('ALL');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | 'ALL'>('ALL');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DeliverableTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<DeliverableTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Apply Template modal state
  const [applyingTemplate, setApplyingTemplate] = useState<DeliverableTemplate | null>(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const canCreateTemplate = user?.role === 'CEO' || user?.role === 'GROUP_MANAGER' || user?.role === 'MANAGER';

  // Fetch templates
  const { data: templates = [], isLoading, refetch } = useQuery<DeliverableTemplate[]>({
    queryKey: ['deliverable-templates', categoryFilter, isActiveFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (categoryFilter !== 'ALL') params.append('category', categoryFilter);
      if (isActiveFilter !== 'ALL') params.append('isActive', String(isActiveFilter));

      const response = await fetch(`${API_URL}/deliverable-templates?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      return data.success ? data.data : [];
    },
  });

  // Fetch leads for apply modal
  const { data: allLeads = [] } = useQuery<Lead[]>({
    queryKey: ['leads-for-template-apply'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/leads`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch leads');
      const result = await response.json();
      return result.data || result.leads || result;
    },
    enabled: !!applyingTemplate,
  });

  // Filter leads by search in apply modal
  const filteredLeads = useMemo(() => {
    if (!leadSearchQuery.trim()) return allLeads;
    const q = leadSearchQuery.toLowerCase();
    return allLeads.filter(
      (lead) =>
        lead.name.toLowerCase().includes(q) ||
        lead.company.toLowerCase().includes(q) ||
        (lead.email && lead.email.toLowerCase().includes(q))
    );
  }, [allLeads, leadSearchQuery]);

  // Filter templates by search query
  const filteredTemplates = templates.filter((template) =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const stats = {
    total: templates.length,
    active: templates.filter((t) => t.isActive).length,
    public: templates.filter((t) => t.isPublic).length,
    private: templates.filter((t) => !t.isPublic).length,
  };

  const extractErrorMessage = (error: Record<string, unknown>): string => {
    // If the API returns field-specific validation errors in an `errors` array
    if (Array.isArray(error.errors) && error.errors.length > 0) {
      const fieldMessages = error.errors.map((err: Record<string, unknown>) => {
        const field = err.field || err.path;
        const msg = err.message || err.msg || String(err);
        return field ? `${field}: ${msg}` : String(msg);
      });
      return fieldMessages.join('; ');
    }
    return (error.message as string) || 'An unexpected error occurred';
  };

  const handleCreateTemplate = async (data: CreateTemplateData) => {
    const response = await fetch(`${API_URL}/deliverable-templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error));
    }

    await refetch();
  };

  const handleEditTemplate = (template: DeliverableTemplate) => {
    setEditingTemplate(template);
  };

  const handleUpdateTemplate = async (data: CreateTemplateData) => {
    if (!editingTemplate) return;

    const response = await fetch(`${API_URL}/deliverable-templates/${editingTemplate.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error));
    }

    setEditingTemplate(null);
    await refetch();
  };

  const handleDeleteTemplate = async () => {
    if (!deletingTemplate) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await fetch(`${API_URL}/deliverable-templates/${deletingTemplate.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(error));
      }

      setDeletingTemplate(null);
      await refetch();
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewTemplate = (template: DeliverableTemplate) => {
    // TODO: Implement a read-only view modal showing full template details,
    // checklist items, proof requirements, and usage history.
    // For now, open edit modal in a read-only capacity is not ideal,
    // so this remains a stub until a dedicated ViewTemplateModal is built.
    setEditingTemplate(template);
  };

  const handleApplyTemplate = (template: DeliverableTemplate) => {
    setApplyingTemplate(template);
    setSelectedLeadIds([]);
    setLeadSearchQuery('');
  };

  const toggleLeadSelection = (leadId: string) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleConfirmApply = async () => {
    if (!applyingTemplate || selectedLeadIds.length === 0) return;

    setIsApplying(true);
    let successCount = 0;
    let failCount = 0;

    for (const leadId of selectedLeadIds) {
      try {
        const response = await fetch(`${API_URL}/deliverables`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            leadId,
            templateId: applyingTemplate.id,
            title: applyingTemplate.defaultTitle || applyingTemplate.name,
            description: applyingTemplate.defaultDescription || applyingTemplate.description,
            category: applyingTemplate.category,
            priority: applyingTemplate.priority,
            status: 'PENDING',
          }),
        });

        if (!response.ok) {
          failCount++;
        } else {
          successCount++;
        }
      } catch {
        failCount++;
      }
    }

    setIsApplying(false);
    setApplyingTemplate(null);
    setSelectedLeadIds([]);

    if (successCount > 0) {
      toast({
        title: 'Template Applied',
        description: `Created ${successCount} deliverable${successCount > 1 ? 's' : ''} from "${applyingTemplate.name}".${failCount > 0 ? ` ${failCount} failed.` : ''}`,
      });
    }

    if (failCount > 0 && successCount === 0) {
      toast({
        title: 'Failed to Apply Template',
        description: `Could not create deliverables for the selected leads. Please try again.`,
        variant: 'destructive',
      });
    }

    await refetch();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Deliverable Templates
            </h1>
            <p className="text-gray-600 mt-1">
              Create and manage reusable deliverable templates
            </p>
          </div>
          {canCreateTemplate && (
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Template
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Templates</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Public</p>
                <p className="text-2xl font-bold text-gray-900">{stats.public}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Lock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Private</p>
                <p className="text-2xl font-bold text-gray-900">{stats.private}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as DeliverableCategory | 'ALL')}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Categories</option>
              <option value="PROPOSAL">Proposal</option>
              <option value="CONTRACT">Contract</option>
              <option value="IMPLEMENTATION">Implementation</option>
              <option value="INSTALLATION">Installation</option>
              <option value="TRAINING">Training</option>
              <option value="SUPPORT">Support</option>
              <option value="DOCUMENTATION">Documentation</option>
              <option value="LICENSE">License</option>
              <option value="SERVICE">Service</option>
              <option value="EQUIPMENT">Equipment</option>
              <option value="CONSULTATION">Consultation</option>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="OTHER">Other</option>
            </select>

            {/* Active Filter */}
            <select
              value={isActiveFilter === 'ALL' ? 'ALL' : String(isActiveFilter)}
              onChange={(e) =>
                setIsActiveFilter(
                  e.target.value === 'ALL' ? 'ALL' : e.target.value === 'true'
                )
              }
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Templates List */}
        <TemplateList
          templates={filteredTemplates}
          isLoading={isLoading}
          onView={handleViewTemplate}
          onEdit={handleEditTemplate}
          onApply={handleApplyTemplate}
          onDelete={(template) => setDeletingTemplate(template)}
          canEdit={canCreateTemplate}
        />

        {/* Create Template Modal */}
        <CreateTemplateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateTemplate}
        />

        {/* Edit Template Modal */}
        {editingTemplate && (
          <CreateTemplateModal
            key={editingTemplate.id}
            isOpen={true}
            onClose={() => setEditingTemplate(null)}
            onSubmit={handleUpdateTemplate}
            initialData={{
              name: editingTemplate.name,
              description: editingTemplate.description,
              category: editingTemplate.category,
              priority: editingTemplate.priority,
              defaultDuration: editingTemplate.defaultDuration,
              defaultTitle: editingTemplate.defaultTitle,
              defaultDescription: editingTemplate.defaultDescription,
              proofRequired: editingTemplate.proofRequired,
              requiredProofTypes: editingTemplate.requiredProofTypes,
              minProofCount: editingTemplate.minProofCount,
              isPublic: editingTemplate.isPublic,
              applyOnStageChange: editingTemplate.applyOnStageChange,
              targetStage: editingTemplate.targetStage,
              checklistItems: editingTemplate.checklistItems?.map((item) => ({
                title: item.title,
                description: item.description,
                sortOrder: item.sortOrder,
                isRequired: item.isRequired,
                requiresProof: item.requiresProof,
              })) || [],
            }}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deletingTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Template</h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">"{deletingTemplate.name}"</span>?
                This action cannot be undone.
              </p>
              {deletingTemplate.usageCount > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    This template has been used {deletingTemplate.usageCount} time(s).
                    Existing deliverables created from it will not be affected.
                  </p>
                </div>
              )}
              {deleteError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-red-800">{deleteError}</p>
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeletingTemplate(null);
                    setDeleteError(null);
                  }}
                  disabled={isDeleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteTemplate}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Apply Template Modal */}
        {applyingTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Apply Template</h3>
                <button
                  onClick={() => {
                    setApplyingTemplate(null);
                    setSelectedLeadIds([]);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Template Preview */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                <p className="text-sm font-semibold text-blue-900">{applyingTemplate.name}</p>
                {applyingTemplate.description && (
                  <p className="text-sm text-blue-700 mt-1">{applyingTemplate.description}</p>
                )}
                <div className="flex gap-3 mt-2">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                    {applyingTemplate.category}
                  </span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                    {applyingTemplate.priority} priority
                  </span>
                  {applyingTemplate.defaultDuration > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      {applyingTemplate.defaultDuration} days
                    </span>
                  )}
                </div>
              </div>

              {/* Lead Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads by name, company, or email..."
                  value={leadSearchQuery}
                  onChange={(e) => setLeadSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Selected count */}
              {selectedLeadIds.length > 0 && (
                <p className="text-sm text-blue-600 font-medium mb-2">
                  {selectedLeadIds.length} lead{selectedLeadIds.length > 1 ? 's' : ''} selected
                </p>
              )}

              {/* Lead List */}
              <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 min-h-0 mb-4">
                {allLeads.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-gray-400" />
                    Loading leads...
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">
                    No leads match your search.
                  </div>
                ) : (
                  filteredLeads.map((lead) => {
                    const isSelected = selectedLeadIds.includes(lead.id);
                    return (
                      <label
                        key={lead.id}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleLeadSelection(lead.id)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {lead.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {lead.company}
                            {lead.email ? ` - ${lead.email}` : ''}
                          </p>
                        </div>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            lead.stage === 'WON'
                              ? 'bg-green-100 text-green-800'
                              : lead.stage === 'LOST'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {lead.stage}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setApplyingTemplate(null);
                    setSelectedLeadIds([]);
                  }}
                  disabled={isApplying}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmApply}
                  disabled={isApplying || selectedLeadIds.length === 0}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                >
                  {isApplying ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Applying...
                    </span>
                  ) : (
                    `Apply to ${selectedLeadIds.length || 0} Lead${selectedLeadIds.length !== 1 ? 's' : ''}`
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
